import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { vendorProfileApi, VendorProfileUpdatePayload } from '@/services/vendorProfileApi';

const initialState: VendorProfileUpdatePayload = {
  firstName: '',
  lastName: '',
  phone: '',
  fullAddress: '',
  province: '',
  district: '',
  neighborhood: '',
  postalCode: '',
  businessType: 'individual',
  companyName: '',
  taxOffice: '',
  taxNumber: '',
  bankName: '',
  iban: '',
  accountHolderName: '',
  logoUrl: '',
  description: '',
  website: '',
  password: '',
};

const VendorProfilePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [formData, setFormData] = useState<VendorProfileUpdatePayload>(initialState);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        const profile = await vendorProfileApi.getProfile();
        setEmail(profile.email || '');
        setFormData({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
          fullAddress: profile.address?.fullAddress || '',
          province: profile.address?.province || '',
          district: profile.address?.district || '',
          neighborhood: profile.address?.neighborhood || '',
          postalCode: profile.address?.postalCode || '',
          businessType: profile.taxInfo?.businessType || 'individual',
          companyName: profile.taxInfo?.companyName || '',
          taxOffice: profile.taxInfo?.taxOffice || '',
          taxNumber: profile.taxInfo?.taxNumber || '',
          bankName: profile.bankInfo?.bankName || '',
          iban: profile.bankInfo?.iban || '',
          accountHolderName: profile.bankInfo?.accountHolderName || '',
          logoUrl: profile.logoUrl || '',
          description: profile.description || '',
          website: profile.website || '',
          password: '',
        });
      } catch (error: any) {
        console.error('Vendor profil yükleme hatası:', error);
        toast.error(error.message || 'Profil yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    void loadProfile();
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formData.password && formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }

    setSaving(true);
    try {
      const updatedProfile = await vendorProfileApi.updateProfile(formData);
      setEmail(updatedProfile.email || email);
      setFormData((previous) => ({ ...previous, password: '' }));
      toast.success('Profil güncellendi');
    } catch (error: any) {
      console.error('Vendor profil güncelleme hatası:', error);
      toast.error(error.message || 'Profil güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-12"><div className="animate-spin text-4xl">⏳</div></div>;
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-3xl font-bold text-dark dark:text-light">Profilim</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Kendi vendor bilgilerinizi buradan güncelleyebilirsiniz</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-4">
          <h2 className="text-xl font-semibold">Temel Bilgiler</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Ad</label>
              <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Soyad</label>
              <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">E-posta</label>
              <input type="email" value={email} disabled className="input-field opacity-70" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Telefon</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Website</label>
              <input type="text" name="website" value={formData.website || ''} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Logo URL</label>
              <input type="text" name="logoUrl" value={formData.logoUrl || ''} onChange={handleChange} className="input-field" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Açıklama</label>
              <textarea name="description" value={formData.description || ''} onChange={handleChange} rows={4} className="input-field" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Yeni Şifre</label>
              <input type="password" name="password" minLength={6} value={formData.password || ''} onChange={handleChange} className="input-field" />
              <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız mevcut şifre değişmez.</p>
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-xl font-semibold">Adres</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Açık Adres</label>
              <input type="text" name="fullAddress" value={formData.fullAddress} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">İl</label>
              <input type="text" name="province" value={formData.province} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">İlçe</label>
              <input type="text" name="district" value={formData.district} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Mahalle</label>
              <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Posta Kodu</label>
              <input type="text" name="postalCode" value={formData.postalCode || ''} onChange={handleChange} className="input-field" />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-xl font-semibold">Vergi Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">İşletme Tipi</label>
              <select name="businessType" value={formData.businessType} onChange={handleChange} className="input-field">
                <option value="individual">Bireysel</option>
                <option value="company">Şirket</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Şirket Adı</label>
              <input type="text" name="companyName" value={formData.companyName || ''} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Vergi Dairesi</label>
              <input type="text" name="taxOffice" value={formData.taxOffice} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Vergi Numarası</label>
              <input type="text" name="taxNumber" value={formData.taxNumber} onChange={handleChange} className="input-field" />
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-xl font-semibold">Banka Bilgileri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Banka Adı</label>
              <input type="text" name="bankName" value={formData.bankName} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">IBAN</label>
              <input type="text" name="iban" value={formData.iban} onChange={handleChange} className="input-field" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Hesap Sahibi</label>
              <input type="text" name="accountHolderName" value={formData.accountHolderName} onChange={handleChange} className="input-field" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-60">
            <Save size={18} />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default VendorProfilePage;